import { useState, useMemo, useCallback, useEffect } from 'react'
import { useRecoilValue } from 'recoil'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { loadTossPayments } from '@tosspayments/payment-sdk'
import { v4 as uuidV4 } from 'uuid'
import { message, Modal, notification, Space, Typography } from 'antd'

import vendorAtom from '@src/atom/vendor'
import { APP_URL, TOSS_CLIENT_KEY } from '@src/utils/constants'
import { GetCardQueryKey, CardDto } from '@src/type/payment'
import { useCreateBillingKeyMutate } from '@src/hooks/queries/payment/useCreateBillingKeyMutate'
import { useDeleteCardMutate } from '@src/hooks/queries/payment/useDeleteCardMutate'
import { useSavePromotionCodeRecordMutate } from '@src/hooks/queries/promotion/useSavePromotionCodeRecordMutate'
import { useGetCardQuery } from '@src/hooks/queries/payment/useGetCardQuery'
import { useGetCompanyChallengesQuery } from '@src/hooks/queries/challenge/useGetCompanyChallengesQuery'
import { GetCompanyChallengesQueryKey } from '@src/type/challenge'
import {
	GetRecentPromotionCodeRecordQueryKey,
	RecentPromotionCodeRecordDto,
} from '@src/type/promotion'
import { useGetRecentPromotionCodeRecordQuery } from '@src/hooks/queries/promotion/useGetRecentPromotionCodeRecordQuery'

const { confirm } = Modal
const { Text } = Typography

export function usePaymentInfoPage() {
	const navigate = useNavigate()

	const [searchParams, setSearchParams] = useSearchParams()

	const [tossAuthKey, tossCustomerKey, tossErrorCode, tossErrorMessage] = useMemo<
		[string | undefined, string | undefined, string | undefined, string | undefined]
	>(() => {
		const authKey = searchParams.get('authKey') || undefined
		const customerKey = searchParams.get('customerKey') || undefined
		const errorCode = searchParams.get('code') || undefined
		const errorMessage = searchParams.get('message') || undefined

		return [authKey, customerKey, errorCode, errorMessage]
	}, [searchParams])

	const isSuccessUrl = useMemo<boolean>(
		() => !!tossAuthKey && !!tossCustomerKey,
		[tossAuthKey, tossCustomerKey],
	)

	const isFailUrl = useMemo<boolean>(
		() => !!tossErrorCode && !!tossErrorMessage,
		[tossErrorCode, tossErrorMessage],
	)

	const vendor = useRecoilValue(vendorAtom)

	const vendorNo = useMemo(() => {
		const parsedVendorNo = Number.parseInt(vendor.no, 10)
		const no = Number.isNaN(parsedVendorNo) ? undefined : parsedVendorNo

		return no
	}, [vendor])

	const cardQueryKey = useMemo<GetCardQueryKey>(() => ['getCard', vendorNo], [vendorNo])

	const { data: cardResBody } = useGetCardQuery({ queryKey: cardQueryKey })

	const card = useMemo<CardDto | null | undefined>(() => {
		if (!cardResBody) {
			return undefined
		}

		if (cardResBody.data.card_info_list.length <= 0) {
			return null
		}

		return cardResBody.data.card_info_list[0]
	}, [cardResBody])

	const cardNo = useMemo(() => {
		if (!card) {
			return undefined
		}

		const parsedCardNo = Number.parseInt(card.no, 10)
		const no = Number.isNaN(parsedCardNo) ? undefined : parsedCardNo

		return no
	}, [card])

	const { mutateAsync: deleteCardMutate } = useDeleteCardMutate({
		invalidateQueryKey: cardQueryKey,
	})

	const { mutateAsync: createBillingKeyMutate } = useCreateBillingKeyMutate({
		invalidateQueryKey: cardQueryKey,
	})

	const [hasUnknownError, setHasUnknownError] = useState(false)

	const [creatingErrorMessage, setCreatingErrorMessage] = useState<string>()

	const [promotionCode, setPromotionCode] = useState('')
	const [isInvalidPromotionCode, setIsInvalidPromotionCode] = useState(false)

	// const canSavePromotionCode = useMemo(() => !!promotionCode, [promotionCode])

	const onChangePromotionCode = useCallback((changedValue: string) => {
		setPromotionCode(changedValue)
		setIsInvalidPromotionCode(false)
	}, [])

	const [isPromotionCodeModalOpened, setIsPromotionCodeModalOpened] = useState(false)

	const onClosePromotionCodeModal = useCallback(() => {
		setIsPromotionCodeModalOpened(false)
		setPromotionCode('')
		setIsInvalidPromotionCode(false)
	}, [])

	const onClickCardRegistration = useCallback(() => {
		setIsPromotionCodeModalOpened(true)
	}, [])

	const onClickSkipPromotionCode = useCallback(async () => {
		setIsPromotionCodeModalOpened(false)
		setPromotionCode('')
		setIsInvalidPromotionCode(false)

		try {
			await openTossPayments({
				onUserCancel: async () => {
					await message.info('카드 등록 취소', 1)
				},
			})
		} catch (e) {
			console.log(e)
			setHasUnknownError(true)
		}
	}, [])

	const { mutateAsync: savePromotionCodeRecordMutate, isLoading: isSavingPromotionCodeRecord } =
		useSavePromotionCodeRecordMutate()

	const onClickSavePromotionCode = useCallback(async () => {
		if (!promotionCode.startsWith('motiner')) {
			setIsInvalidPromotionCode(true)
			return
		}

		try {
			await savePromotionCodeRecordMutate({ promotion_code: promotionCode })
		} catch (e) {
			console.log(e)

			setIsPromotionCodeModalOpened(false)
			setPromotionCode('')
			setIsInvalidPromotionCode(false)

			await message.error('쿠폰 등록 실패')

			return
		}

		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		message.success('쿠폰 등록 성공')

		setIsPromotionCodeModalOpened(false)
		setPromotionCode('')
		setIsInvalidPromotionCode(false)

		try {
			await openTossPayments({
				onUserCancel: async () => {
					await message.info('카드 등록 취소', 1)
				},
			})
		} catch (e) {
			console.log(e)
			setHasUnknownError(true)
		}
	}, [promotionCode, savePromotionCodeRecordMutate])

	const onClickCardDeletion = useCallback(() => {
		if (!cardNo) {
			return
		}

		confirm({
			centered: true,
			title: '등록 카드를 삭제 할까요?',
			icon: null,
			async onOk() {
				try {
					await deleteCardMutate(cardNo)
					// eslint-disable-next-line @typescript-eslint/no-floating-promises
					message.success('등록 카드가 삭제되었어요')
				} catch (e) {
					console.log(e)
					// eslint-disable-next-line @typescript-eslint/no-floating-promises
					message.error('카드 삭제 실패')
				}
			},
			okText: '삭제',
			cancelText: '취소',
			okButtonProps: {
				danger: true,
			},
		})
	}, [deleteCardMutate, cardNo])

	const [isOpenRegisteringModal, setIsOpenRegisteringModal] = useState(isSuccessUrl)

	const companyChallengesQueryKey = useMemo<GetCompanyChallengesQueryKey>(() => {
		return ['getCompanyChallenges', vendorNo, 1]
	}, [vendorNo])

	const { data: companyChallengesResBody } = useGetCompanyChallengesQuery({
		queryKey: companyChallengesQueryKey,
	})

	const hasTemporary = useMemo<boolean | undefined>(() => {
		if (!companyChallengesResBody) {
			return undefined
		}

		const {
			data: { items: companyChallengeDtos },
		} = companyChallengesResBody

		return companyChallengeDtos.some((dto) => dto.is_temporary)
	}, [companyChallengesResBody])

	useEffect(() => {
		if (
			!isSuccessUrl ||
			!vendor.no ||
			!vendor.brandName ||
			!vendor.username ||
			hasTemporary === undefined
		) {
			return
		}

		const create = async () => {
			try {
				if (!tossAuthKey || !tossCustomerKey) {
					throw new Error('auth key, customer key가 없지만 빌링키 생성 시도됨')
				}

				await createBillingKeyMutate({
					auth_key: tossAuthKey,
					customer_key: tossCustomerKey,
				})
			} catch (e) {
				console.log(e)
				setCreatingErrorMessage(
					'암호화된 카드 입력 정보를 업로드하지 못했습니다. 다시 시도해주세요. 문제가 지속될 경우 고객센터에 문의해주세요.',
				)
			} finally {
				setIsOpenRegisteringModal(false)

				setSearchParams(
					(prevSearchParams) => {
						prevSearchParams.delete('authKey')
						prevSearchParams.delete('customerKey')

						return prevSearchParams
					},
					{
						replace: true,
					},
				)
			}

			try {
				// eslint-disable-next-line @typescript-eslint/no-floating-promises
				message.success('카드 등록 성공!')

				notification.config({
					duration: 0,
					placement: 'bottomLeft',
					bottom: 154,
				})
				notification.open({
					message: (
						<Space direction="horizontal" size="large">
							<Text>{`💁‍♀️ 이제 ${
								hasTemporary ? '임시저장된 ' : ''
							}챌린지를 생성할 수 있어요!`}</Text>
							<Text
								underline
								style={{ cursor: 'pointer' }}
								onClick={() => {
									navigate(`${hasTemporary ? '/challenge' : '/challenge/hotdeal-registration'}`)
									notification.destroy()
								}}
							>
								바로 가기
							</Text>
						</Space>
					),
					className: 'motiners-notification',
				})
			} catch (e) {
				console.log(e)
			}
		}

		create().catch((e) => console.log(e))
	}, [
		isSuccessUrl,
		tossAuthKey,
		tossCustomerKey,
		createBillingKeyMutate,
		setSearchParams,
		vendor,
		hasTemporary,
		navigate,
	])

	const errorMessage = useMemo<string | undefined>(() => {
		const knownMessage = tossErrorMessage || creatingErrorMessage

		if (knownMessage) {
			return knownMessage
		}

		if (hasUnknownError) {
			return '알 수 없는 오류가 발생했습니다. 새로고침 후 다시 시도해주세요. 문제가 지속될 경우 고객센터에 문의해주세요.'
		}

		return undefined
	}, [tossErrorMessage, creatingErrorMessage, hasUnknownError])

	const onCloseErrorMessage = useCallback(() => {
		setCreatingErrorMessage(undefined)
		setHasUnknownError(false)
	}, [])

	const recentPromotionCodeQueryKey = useMemo<GetRecentPromotionCodeRecordQueryKey>(
		() => ['get_recent_promotion_code_record', vendorNo],
		[vendorNo],
	)

	const { data: recentPromotionCodeResBody } = useGetRecentPromotionCodeRecordQuery({
		queryKey: recentPromotionCodeQueryKey,
	})

	const recentPromotionCode = useMemo<RecentPromotionCodeRecordDto | null | undefined>(() => {
		if (!recentPromotionCodeResBody) {
			return undefined
		}

		return recentPromotionCodeResBody.data.recent_record
	}, [recentPromotionCodeResBody])

	return {
		card,
		onClickCardRegistration,
		onClickCardDeletion,
		isOpenRegisteringModal,
		errorMessage,
		onCloseErrorMessage,
		promotionCode,
		isInvalidPromotionCode,
		// canSavePromotionCode,
		onChangePromotionCode,
		onClickSkipPromotionCode,
		onClickSavePromotionCode,
		isSavingPromotionCodeRecord,
		isPromotionCodeModalOpened,
		onClosePromotionCodeModal,
		recentPromotionCode,
	}
}

async function openTossPayments({ onUserCancel }: { onUserCancel?: () => void | Promise<void> }) {
	const tossPayments = await loadTossPayments(TOSS_CLIENT_KEY || 'error key')

	try {
		await tossPayments.requestBillingAuth('카드', {
			customerKey: uuidV4(),
			successUrl: `${APP_URL}/payment/subscribe/info`,
			failUrl: `${APP_URL}/payment/subscribe/info`,
		})
	} catch (e) {
		if ((e as Error & { code: string }).code === 'USER_CANCEL' && onUserCancel) {
			await onUserCancel()
		} else {
			throw e
		}
	}
}
